import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { ClipboardCheck, Star, ArrowLeft } from "lucide-react";
import { toast } from "sonner";

const DashboardWeeklyReview = () => {
  const [loading, setLoading] = useState(true);
  const [currentReview, setCurrentReview] = useState<any>(null);
  const [wins, setWins] = useState("");
  const [challenges, setChallenges] = useState("");
  const [lessonsLearned, setLessonsLearned] = useState("");
  const [nextWeekGoals, setNextWeekGoals] = useState("");
  const [overallRating, setOverallRating] = useState(3);

  useEffect(() => {
    loadCurrentWeekReview();
  }, []);

  const getWeekStartDate = () => {
    const today = new Date();
    const day = today.getDay();
    const diff = today.getDate() - day + (day === 0 ? -6 : 1); // Adjust for Monday start
    const monday = new Date(today.setDate(diff));
    return monday.toISOString().split('T')[0];
  };

  const loadCurrentWeekReview = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    const weekStart = getWeekStartDate();

    const { data, error } = await supabase
      .from("weekly_reviews")
      .select("*")
      .eq("user_id", user.id)
      .eq("week_start_date", weekStart)
      .maybeSingle();

    if (error) {
      console.error("Error loading review:", error);
    } else if (data) {
      setCurrentReview(data);
      setWins(data.wins || "");
      setChallenges(data.challenges || "");
      setLessonsLearned(data.lessons_learned || "");
      setNextWeekGoals(data.next_week_goals || "");
      setOverallRating(data.overall_rating || 3);
    }
    setLoading(false);
  };

  const handleSave = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    const weekStart = getWeekStartDate();

    const reviewData = {
      user_id: user.id,
      week_start_date: weekStart,
      wins,
      challenges,
      lessons_learned: lessonsLearned,
      next_week_goals: nextWeekGoals,
      overall_rating: overallRating,
    };

    const { error } = await supabase
      .from("weekly_reviews")
      .upsert(reviewData);

    if (error) {
      toast.error("Failed to save review");
      console.error(error);
    } else {
      toast.success("Weekly review saved!");
      loadCurrentWeekReview();
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 max-w-4xl">
      <Link to="/dashboard">
        <Button variant="ghost" className="mb-6">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Dashboard
        </Button>
      </Link>

      <div className="flex items-center gap-3 mb-6">
        <ClipboardCheck className="w-8 h-8 text-primary" />
        <h1 className="text-3xl font-bold">Weekly Review</h1>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Week of {new Date(getWeekStartDate()).toLocaleDateString()}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div>
            <Label htmlFor="wins">🎉 Wins & Accomplishments</Label>
            <Textarea
              id="wins"
              value={wins}
              onChange={(e) => setWins(e.target.value)}
              placeholder="What went well this week? What are you proud of?"
              rows={4}
              className="mt-2"
            />
          </div>

          <div>
            <Label htmlFor="challenges">💪 Challenges & Obstacles</Label>
            <Textarea
              id="challenges"
              value={challenges}
              onChange={(e) => setChallenges(e.target.value)}
              placeholder="What challenges did you face? What held you back?"
              rows={4}
              className="mt-2"
            />
          </div>

          <div>
            <Label htmlFor="lessons">📚 Lessons Learned</Label>
            <Textarea
              id="lessons"
              value={lessonsLearned}
              onChange={(e) => setLessonsLearned(e.target.value)}
              placeholder="What did you learn this week? What insights did you gain?"
              rows={4}
              className="mt-2"
            />
          </div>

          <div>
            <Label htmlFor="next-goals">🎯 Goals for Next Week</Label>
            <Textarea
              id="next-goals"
              value={nextWeekGoals}
              onChange={(e) => setNextWeekGoals(e.target.value)}
              placeholder="What do you want to focus on next week?"
              rows={4}
              className="mt-2"
            />
          </div>

          <div>
            <Label>Overall Week Rating</Label>
            <div className="flex gap-2 mt-4">
              {[1, 2, 3, 4, 5].map((rating) => (
                <button
                  key={rating}
                  onClick={() => setOverallRating(rating)}
                  className={`p-2 transition-all ${
                    rating <= overallRating
                      ? "text-yellow-500"
                      : "text-gray-300"
                  }`}
                >
                  <Star className="w-8 h-8" fill={rating <= overallRating ? "currentColor" : "none"} />
                </button>
              ))}
            </div>
          </div>

          <Button onClick={handleSave} className="w-full">
            {currentReview ? "Update Review" : "Save Review"}
          </Button>

          {currentReview && (
            <p className="text-sm text-muted-foreground text-center">
              Last updated: {new Date(currentReview.updated_at).toLocaleString()}
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default DashboardWeeklyReview;
